import { useEffect, useMemo, useState } from 'react';

import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  FlagOutlined,
  MoreOutlined,
  PlusOutlined,
  SettingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  App,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  Popover,
  Row,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  QuizContentType,
  QuizContentTypeOption,
  QuizDetail,
  QuizListItem,
  QuizSettings,
  QuizTypeOption,
  QuizzesListParams,
  QuizzesListResponse,
} from 'models/Quiz';

import { QuizService } from 'services/QuizService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../components/Layout/Layout';
import appI18n from '../../config/i18n';
import CreateQuizModal from './Components/CreateQuizModal';
import QuizSettingsModal from './Components/QuizSettingsModal';

import './Quizzes.scss';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'quizzes.pageSize';
const DEFAULT_COUNTRY = 'CL';
const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch (e) {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

function Quizzes() {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message, modal } = App.useApp();

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<QuizListItem[]>([]);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [total, setTotal] = useState<number>(0);

  // Búsqueda por nombre (se envía como `q` al backend).
  const [term, setTerm] = useState<string>('');

  // Orden (server-side). Default del backend: createdAt desc.
  const [sortField, setSortField] = useState<'createdAt' | 'name'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Filtros aplicados (los que se mandan al backend)
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [active, setActive] = useState<boolean | undefined>(undefined);
  const [contentType, setContentType] = useState<QuizContentType | undefined>(
    undefined
  );
  const [quizType, setQuizType] = useState<string | undefined>(undefined);

  // Filtros en borrador (los que se editan dentro del popover)
  const [draftCountry, setDraftCountry] = useState<string>(DEFAULT_COUNTRY);
  const [draftActive, setDraftActive] = useState<boolean | undefined>(
    undefined
  );
  const [draftContentType, setDraftContentType] = useState<
    QuizContentType | undefined
  >(undefined);
  const [draftQuizType, setDraftQuizType] = useState<string | undefined>(
    undefined
  );

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  // Catálogos
  const [quizTypes, setQuizTypes] = useState<QuizTypeOption[]>([]);
  const [contentTypes, setContentTypes] = useState<QuizContentTypeOption[]>([]);
  const [settings, setSettings] = useState<QuizSettings | null>(null);

  // Modales
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [createMode, setCreateMode] = useState<'create' | 'edit'>('create');
  const [editingQuiz, setEditingQuiz] = useState<QuizDetail | null>(null);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] =
    useState<boolean>(false);

  const quizTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    quizTypes.forEach((item) => {
      map[item.code] = item.label;
    });
    return map;
  }, [quizTypes]);

  const contentTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    contentTypes.forEach((item) => {
      map[item.code] = item.label;
    });
    return map;
  }, [contentTypes]);

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [country, active, contentType, quizType, term, sortField, sortDir]);

  useEffect(() => {
    fetchData();
  }, [
    page,
    pageSize,
    country,
    active,
    contentType,
    quizType,
    term,
    sortField,
    sortDir,
  ]);

  const loadCatalogs = async () => {
    const [typesRes, contentRes, settingsRes] = await Promise.all([
      QuizService.getQuizTypes(),
      QuizService.getContentTypes(),
      QuizService.getQuizSettings(),
    ]);

    if (typesRes.success) {
      setQuizTypes(typesRes.data as QuizTypeOption[]);
    }
    if (contentRes.success) {
      setContentTypes(contentRes.data as QuizContentTypeOption[]);
    }
    if (settingsRes.success) {
      setSettings(settingsRes.data as QuizSettings);
    }
  };

  const fetchData = async (silent = false) => {
    if (!silent) {
      setTableLoading(true);
    }

    const params: QuizzesListParams = {
      country,
      page,
      pageSize,
      sort: sortField,
      dir: sortDir,
    };
    if (active !== undefined) params.active = active;
    if (contentType) params.contentType = contentType;
    if (quizType) params.quizType = quizType;
    if (term) params.q = term;

    const response = await QuizService.getQuizzes(params);

    if (response.success) {
      const data = response.data as QuizzesListResponse;
      setDataSource(data.data);
      setTotal(data.totalRows);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.quizzes.scene.errors.fetch_list'),
        3.5
      );
    }

    if (!silent) {
      setTableLoading(false);
    }
  };

  // Aplica el término de búsqueda (Enter / clic en buscar / limpiar con la X).
  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleTableChange: TableProps<QuizListItem>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
      try {
        localStorage.setItem(
          PAGE_SIZE_STORAGE_KEY,
          String(pagination.pageSize)
        );
      } catch (e) {
        // localStorage puede no estar disponible
      }
    }

    const sorterLocal = sorter as any;
    const order = sorterLocal?.order as 'ascend' | 'descend' | undefined;
    const field = (sorterLocal?.columnKey || sorterLocal?.field) as
      | string
      | undefined;
    if (order && (field === 'name' || field === 'createdAt')) {
      setSortField(field as 'name' | 'createdAt');
      setSortDir(order === 'ascend' ? 'asc' : 'desc');
    } else {
      // Sin orden explícito → vuelve al default del backend.
      setSortField('createdAt');
      setSortDir('desc');
    }
  };

  const openEditModal = async (row: QuizListItem) => {
    // Abrimos el modal de inmediato y mostramos un skeleton mientras llega
    // el detalle desde el backend.
    setEditingQuiz(null);
    setCreateMode('edit');
    setEditLoading(true);
    setIsCreateModalOpen(true);

    const response = await QuizService.getQuizById(row.id);
    if (response.success) {
      setEditingQuiz(response.data as QuizDetail);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.quizzes.scene.errors.fetch_detail'),
        3.5
      );
      setIsCreateModalOpen(false);
    }
    setEditLoading(false);
  };

  const openCreateModal = () => {
    setEditingQuiz(null);
    setCreateMode('create');
    setIsCreateModalOpen(true);
  };

  const handleDelete = (row: QuizListItem) => {
    modal.confirm({
      title: t('scenes.quizzes.scene.delete_confirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('scenes.quizzes.scene.delete_confirm.content', {
        name: row.name,
      }),
      okText: t('scenes.quizzes.scene.delete_confirm.ok'),
      cancelText: t('scenes.quizzes.scene.delete_confirm.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await QuizService.deleteQuiz(row.id);
        if (response.success) {
          message.success(
            t('scenes.quizzes.scene.delete_confirm.success'),
            2.5
          );
          // Si era el único de la página y no es la primera, retrocedemos una
          // página; si no, recargamos la página actual.
          if (dataSource.length === 1 && page > 1) {
            setPage((p) => p - 1);
          } else {
            fetchData(true);
          }
        } else {
          const error = response.data as ApiErrorData;
          const code = String(error.code || '');
          const errMsg = String(error.message || '');
          const hasAttempts =
            code.includes('has_attempts') || /attempt|intento/i.test(errMsg);
          if (hasAttempts) {
            message.error(
              t('scenes.quizzes.scene.delete_confirm.has_attempts'),
              4.5
            );
          } else {
            message.error(
              (error.message as string) ||
                t('scenes.quizzes.scene.delete_confirm.error'),
              3.5
            );
          }
        }
      },
    });
  };

  const handleToggleStatus = (row: QuizListItem) => {
    const newActive = !row.active;
    const key = newActive ? 'activate' : 'deactivate';

    modal.confirm({
      title: t(`scenes.quizzes.scene.status_confirm.title_${key}`),
      icon: <ExclamationCircleOutlined />,
      content: t(`scenes.quizzes.scene.status_confirm.content_${key}`, {
        name: row.name,
      }),
      okText: t(`scenes.quizzes.scene.status_confirm.ok_${key}`),
      cancelText: t('scenes.quizzes.scene.status_confirm.cancel'),
      okButtonProps: { danger: !newActive },
      onOk: async () => {
        const response = await QuizService.updateQuizStatus(row.id, {
          active: newActive,
        });
        if (response.success) {
          message.success(
            t(`scenes.quizzes.scene.status_confirm.success_${key}`),
            2.5
          );
          setDataSource((prev) =>
            prev.map((q) => (q.id === row.id ? { ...q, active: newActive } : q))
          );
        } else {
          const error = response.data as ApiErrorData;
          message.error(
            (error.message as string) ||
              t('scenes.quizzes.scene.status_confirm.error'),
            3.5
          );
        }
      },
    });
  };

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      setDraftCountry(country);
      setDraftActive(active);
      setDraftContentType(contentType);
      setDraftQuizType(quizType);
    }
    setFiltersOpen(open);
  };

  const handleApplyFilters = () => {
    setCountry(draftCountry);
    setActive(draftActive);
    setContentType(draftContentType);
    setQuizType(draftQuizType);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftCountry(DEFAULT_COUNTRY);
    setDraftActive(undefined);
    setDraftContentType(undefined);
    setDraftQuizType(undefined);
  };

  const activeFiltersCount =
    1 + // país siempre cuenta
    (active !== undefined ? 1 : 0) +
    (contentType ? 1 : 0) +
    (quizType ? 1 : 0);

  const getDropdownMenu = (row: QuizListItem): MenuProps['items'] => [
    {
      key: 'edit',
      label: (
        <span>
          <EditOutlined style={{ marginRight: '8px' }} />
          {t('scenes.quizzes.scene.dropdown.edit')}
        </span>
      ),
      onClick: () => openEditModal(row),
    },
    {
      key: 'toggle-status',
      label: (
        <span>
          {row.active ? (
            <StopOutlined style={{ marginRight: '8px' }} />
          ) : (
            <CheckCircleOutlined style={{ marginRight: '8px' }} />
          )}
          {t(
            row.active
              ? 'scenes.quizzes.scene.dropdown.deactivate'
              : 'scenes.quizzes.scene.dropdown.activate'
          )}
        </span>
      ),
      onClick: () => handleToggleStatus(row),
    },
    { type: 'divider' },
    {
      key: 'delete',
      danger: true,
      label: (
        <span>
          <DeleteOutlined style={{ marginRight: '8px' }} />
          {t('scenes.quizzes.scene.dropdown.delete')}
        </span>
      ),
      onClick: () => handleDelete(row),
    },
  ];

  const getCountryOptions = () =>
    COUNTRY_OPTIONS.map((opt) => {
      const code = opt.value.toLowerCase();
      return {
        value: opt.value,
        label: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <img
              src={`https://flagcdn.com/16x12/${code}.png`}
              srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
              width={16}
              height={12}
              alt=""
              style={{ display: 'block' }}
            />
            <span>
              {t(`scenes.quizzes.scene.countries.${opt.value}`, opt.value)}
            </span>
          </span>
        ),
      };
    });

  const statusOptions = [
    { value: true, label: t('scenes.quizzes.scene.statuses.active') },
    { value: false, label: t('scenes.quizzes.scene.statuses.inactive') },
  ];

  const contentTypeOptions = contentTypes.map((item) => ({
    value: item.code,
    label: item.label,
  }));

  const quizTypeOptions = quizTypes.map((item) => ({
    value: item.code,
    label: item.label,
  }));

  const formatDateCell = (isoDate: string): string => {
    const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
    return dayjs(isoDate).format(`${datePart} h:mm A`);
  };

  const filtersContent = (
    <div style={{ width: 320 }}>
      <Form layout="vertical" style={{ marginBottom: 0 }}>
        <Form.Item
          label={t('scenes.quizzes.scene.filters.country_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftCountry}
            onChange={(value) => setDraftCountry(value)}
            options={getCountryOptions()}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.quizzes.scene.filters.status_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftActive}
            onChange={(value) => setDraftActive(value as boolean | undefined)}
            options={statusOptions}
            style={{ width: '100%' }}
            placeholder={t('scenes.quizzes.scene.filters.status_placeholder')}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.quizzes.scene.filters.content_type_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftContentType}
            onChange={(value) =>
              setDraftContentType(value as QuizContentType | undefined)
            }
            options={contentTypeOptions}
            style={{ width: '100%' }}
            placeholder={t(
              'scenes.quizzes.scene.filters.content_type_placeholder'
            )}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.quizzes.scene.filters.quiz_type_label')}
          style={{ marginBottom: 0 }}
        >
          <Select
            value={draftQuizType}
            onChange={(value) => setDraftQuizType(value as string | undefined)}
            options={quizTypeOptions}
            style={{ width: '100%' }}
            placeholder={t(
              'scenes.quizzes.scene.filters.quiz_type_placeholder'
            )}
            allowClear
          />
        </Form.Item>
      </Form>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.quizzes.scene.filters.clear_filters')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.quizzes.scene.filters.apply_filters')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout title={''}>
      <div className="quizzes-scene">
        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.quizzes.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.quizzes.scene.filters.search_placeholder'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="none">
              <Space size={8}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={filtersOpen}
                  onOpenChange={handleFiltersOpenChange}
                  content={filtersContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      <Space size={6}>
                        {t('scenes.quizzes.scene.filters.advanced_filters')}
                      </Space>
                    </Button>
                  </Badge>
                </Popover>
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: [
                      {
                        key: 'settings',
                        label: (
                          <span>
                            <SettingOutlined style={{ marginRight: 8 }} />
                            {t('scenes.quizzes.scene.settings_button')}
                          </span>
                        ),
                        onClick: () => setIsSettingsModalOpen(true),
                      },
                    ],
                  }}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreateModal}
                >
                  {t('scenes.quizzes.scene.create_button')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<QuizListItem>
            dataSource={dataSource}
            bordered
            loading={tableLoading}
            size="small"
            scroll={{ x: 900 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t('scenes.quizzes.scene.table.columns.name')}
              key="name"
              sorter
              sortOrder={
                sortField === 'name'
                  ? sortDir === 'asc'
                    ? 'ascend'
                    : 'descend'
                  : null
              }
              render={(row: QuizListItem) => (
                <div className="quiz-name-cell">
                  <button
                    type="button"
                    className="quiz-id-link"
                    onClick={() => openEditModal(row)}
                  >
                    {row.name}
                  </button>
                  <div className="quiz-name-sub">
                    <FlagOutlined style={{ marginRight: 6 }} />
                    {t(
                      `scenes.quizzes.scene.countries.${row.countryCode}`,
                      row.countryCode
                    )}
                  </div>
                </div>
              )}
            />

            <Column
              title={t('scenes.quizzes.scene.table.columns.type')}
              key="type"
              render={(row: QuizListItem) => (
                <span>{quizTypeMap[row.quizType] || row.quizType}</span>
              )}
            />

            <Column
              title={t('scenes.quizzes.scene.table.columns.content')}
              key="content"
              render={(row: QuizListItem) => (
                <Tag style={{ marginRight: 0 }}>
                  {contentTypeMap[row.contentType] || row.contentType}
                </Tag>
              )}
            />

            <Column
              width={140}
              align="center"
              title={t('scenes.quizzes.scene.table.columns.show_approve')}
              key="show_approve"
              render={(row: QuizListItem) => (
                <span>
                  {row.questionsToShow} / {row.passingScore}
                </span>
              )}
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.quizzes.scene.table.columns.status')}
              key="status"
              render={(row: QuizListItem) => (
                <Tag
                  color={row.active ? 'green' : 'default'}
                  style={{ marginRight: 0 }}
                >
                  {row.active
                    ? t('scenes.quizzes.scene.statuses.active')
                    : t('scenes.quizzes.scene.statuses.inactive')}
                </Tag>
              )}
            />

            <Column
              width={180}
              align="center"
              title={t('scenes.quizzes.scene.table.columns.created')}
              key="createdAt"
              sorter
              sortOrder={
                sortField === 'createdAt'
                  ? sortDir === 'asc'
                    ? 'ascend'
                    : 'descend'
                  : null
              }
              render={(row: QuizListItem) => (
                <span>
                  {row.createdAt ? formatDateCell(row.createdAt) : '—'}
                </span>
              )}
            />

            <Column
              width={70}
              align="center"
              title=""
              key="actions"
              render={(row: QuizListItem) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>
      </div>

      <CreateQuizModal
        isOpen={isCreateModalOpen}
        mode={createMode}
        quiz={editingQuiz}
        loading={editLoading}
        defaultCountry={country}
        quizTypes={quizTypes}
        contentTypes={contentTypes}
        maxVideoSizeMb={settings?.maxVideoSizeMb}
        handleModalCancel={() => setIsCreateModalOpen(false)}
        onSaved={() => {
          message.success(
            createMode === 'edit'
              ? t('scenes.quizzes.scene.create.edit_success')
              : t('scenes.quizzes.scene.create.success'),
            3
          );
          fetchData(true);
        }}
      />

      <QuizSettingsModal
        isOpen={isSettingsModalOpen}
        handleModalCancel={() => setIsSettingsModalOpen(false)}
        onSaved={() => loadCatalogs()}
      />
    </Layout>
  );
}

export default Quizzes;
